import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { App, Button, Select, Table, Tag, Tooltip, Typography, theme } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import axios from 'axios';
import dayjs from 'dayjs';
import { readApiError, vendorApi } from '@/utils/vendorApi';
import { businessUnitLabel, useAuthStore } from '@/store/authStore';
import { fileNameFromTransferId, transferStatusDisplay, triggeredByLabel } from '@/components/transfer/transferStatus';
import { ALL_BUSINESS_UNITS_ID, matchesSelectedBu } from '@/utils/businessUnits';
import { tagFilterParam, tagSelectOptions, useCatalogTags } from '@/utils/tagOptions';
import { ALL_VENDORS, useVendorIdOptions } from '@/utils/vendorIdOptions';
import { TransferItem, TransferListParams, TransferState, TriggeredByFilter } from '@/types';

const { Text } = Typography;

type StatusFilter = TransferState | 'all';
type TriggeredFilter = TriggeredByFilter | 'all';

const STATUS_FILTERS: TransferState[] = [
  'pending',
  'in_flight',
  'completed',
  'failed',
  'retry_scheduled',
  'retries_exhausted',
  'rejected',
  'resubmitted',
];

const NEXT_TOKEN_MISMATCH = new Set([
  'nextToken does not match this query',
  'nextToken does not match vendorId',
]);

function isNextTokenMismatch(error: unknown): boolean {
  if (!axios.isAxiosError(error) || error.response?.status !== 400) {
    return false;
  }
  const { message } = readApiError(error.response.data, '');
  return NEXT_TOKEN_MISMATCH.has(message);
}

function toListParams(
  vendorId: string,
  status: StatusFilter,
  triggered: TriggeredFilter,
  tag: string,
  nextToken?: string,
): TransferListParams {
  return {
    vendorId: vendorId.trim() || undefined,
    status: status === 'all' ? undefined : status,
    triggeredBy: triggered === 'all' ? undefined : triggered,
    tag: tagFilterParam(tag),
    nextToken,
  };
}

function formatTime(value?: string | null): string {
  if (!value) {
    return '—';
  }
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format('MMM DD, YYYY HH:mm') : '—';
}

function initialTransferStatus(): StatusFilter {
  const value = new URLSearchParams(window.location.search).get('status');
  if (value === 'all' || (value !== null && STATUS_FILTERS.includes(value as TransferState))) {
    return value as StatusFilter;
  }
  return 'all';
}

const TransferList: React.FC = () => {
  const { message } = App.useApp();
  const { token } = theme.useToken();
  const navigate = useNavigate();
  const { authorization, currentBusinessUnit } = useAuthStore();
  const tagChoices = useCatalogTags();
  const vendorSource = authorization?.globalAdmin ? 'admin' : 'operator';
  const { vendorIdOptions, optionsLoading, optionRows } = useVendorIdOptions(vendorSource, true);

  const openTransfer = (record: TransferItem) => {
    navigate(`/transfers/detail/${encodeURIComponent(record.transferId)}`);
  };

  const [vendorId, setVendorId] = useState(ALL_VENDORS);
  const [status, setStatus] = useState<StatusFilter>(initialTransferStatus);
  const [triggered, setTriggered] = useState<TriggeredFilter>('all');
  const [tag, setTag] = useState('all');
  const [transfers, setTransfers] = useState<TransferItem[]>([]);
  const [nextToken, setNextToken] = useState<string | undefined>();
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const requestSeq = useRef(0);
  const fetchPageRef = useRef<
    (
      vendorFilter: string,
      statusFilter: StatusFilter,
      triggeredFilter: TriggeredFilter,
      tagFilter: string,
      append: boolean,
      cursor?: string,
    ) => Promise<void>
  >(() => Promise.resolve());

  const fetchPage = useCallback(
    async (
      vendorFilter: string,
      statusFilter: StatusFilter,
      triggeredFilter: TriggeredFilter,
      tagFilter: string,
      append: boolean,
      cursor?: string,
    ) => {
      const seq = ++requestSeq.current;
      if (append) {
        setLoadingMore(true);
      } else {
        setLoading(true);
        setNextToken(undefined);
      }
      try {
        const response = await vendorApi.listTransfers(
          toListParams(vendorFilter, statusFilter, triggeredFilter, tagFilter, cursor)
        );
        if (seq !== requestSeq.current) {
          return;
        }
        setTransfers((prev) => (append ? [...prev, ...response.transfers] : response.transfers));
        setNextToken(response.nextToken);
      } catch (error: unknown) {
        if (seq !== requestSeq.current) {
          return;
        }
        if (cursor && isNextTokenMismatch(error)) {
          void fetchPageRef.current(vendorFilter, statusFilter, triggeredFilter, tagFilter, false);
          return;
        }
        const errorMessage = error instanceof Error ? error.message : 'Failed to load transfers';
        message.error(errorMessage);
        if (!append) {
          setTransfers([]);
          setNextToken(undefined);
        }
      } finally {
        if (seq === requestSeq.current) {
          setLoading(false);
          setLoadingMore(false);
        }
      }
    },
    [message]
  );
  fetchPageRef.current = fetchPage;

  const selectedId = currentBusinessUnit?.id;

  useEffect(() => {
    void fetchPage(vendorId, status, triggered, tag, false);
  }, [vendorId, status, triggered, tag, selectedId, fetchPage]);

  useEffect(() => {
    if (optionRows === null || !vendorId) {
      return;
    }
    const stillListed = vendorIdOptions.some((option) => option.value === vendorId);
    if (!stillListed) {
      setTransfers([]);
      setNextToken(undefined);
      setVendorId(ALL_VENDORS);
    }
  }, [optionRows, vendorIdOptions, vendorId]);

  const handleVendorChange = (next: string) => {
    setTransfers([]);
    setNextToken(undefined);
    setVendorId(next);
  };

  const handleStatusChange = (next: StatusFilter) => {
    setTransfers([]);
    setNextToken(undefined);
    setStatus(next);
  };

  const handleTriggeredChange = (next: TriggeredFilter) => {
    setTransfers([]);
    setNextToken(undefined);
    setTriggered(next);
  };
  const visibleTransfers = selectedId
    ? transfers.filter((row) => matchesSelectedBu(row.bu, selectedId, authorization?.catalog))
    : transfers;
  const filterNote = selectedId && selectedId !== ALL_BUSINESS_UNITS_ID && nextToken
    ? `Filtered to ${businessUnitLabel(selectedId)}. Load more to see additional results.`
    : null;

  const fitContent = {
    onCell: () => ({ style: { whiteSpace: 'nowrap' as const, width: '1%' } }),
    onHeaderCell: () => ({ style: { whiteSpace: 'nowrap' as const, width: '1%' } }),
  };

  const columns: ColumnsType<TransferItem> = [
    {
      title: 'File',
      key: 'file',
      onCell: () => ({ style: { wordBreak: 'break-word' as const, whiteSpace: 'normal' as const } }),
      render: (_value: unknown, record: TransferItem) => {
        const fileName = fileNameFromTransferId(record.transferId);
        if (!fileName) {
          return (
            <Tooltip title="Earlier record without a file name">
              <span style={{ color: token.colorTextSecondary }}>—</span>
            </Tooltip>
          );
        }
        return (
          <span style={{ fontFamily: token.fontFamily, fontWeight: 'normal', wordBreak: 'break-word' }}>
            {fileName}
          </span>
        );
      },
    },
    {
      title: 'Vendor',
      dataIndex: 'vendorId',
      key: 'vendorId',
      ...fitContent,
      render: (value: string | null) => value || '—',
    },
    {
      title: 'Tag',
      dataIndex: 'tag',
      key: 'tag',
      ...fitContent,
      render: (value: string | null | undefined) => value || '—',
    },
    {
      title: 'Direction',
      dataIndex: 'direction',
      key: 'direction',
      ...fitContent,
      render: (value: string | null) => value || '—',
    },
    {
      title: 'User',
      dataIndex: 'triggeredBy',
      key: 'triggeredBy',
      onCell: () => ({ style: { wordBreak: 'break-word' as const } }),
      render: (value: string | null) => {
        const label = triggeredByLabel(value);
        return (
          <Tooltip title={label}>
            <span style={{ wordBreak: 'break-word', whiteSpace: 'normal' }}>{label}</span>
          </Tooltip>
        );
      },
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      ...fitContent,
      render: (value: string) => {
        const display = transferStatusDisplay(value);
        const pendingColor = transferStatusDisplay('pending').color;
        return (
          <Tag
            color={display.color}
            style={display.color === pendingColor ? { color: token.colorText } : undefined}
          >
            {display.label}
          </Tag>
        );
      },
    },
    {
      title: 'Time',
      dataIndex: 'lastTransitionAt',
      key: 'lastTransitionAt',
      ...fitContent,
      render: (value: string | null) => formatTime(value),
    },
  ];

  return (
    <>
      <div style={{ marginBottom: 16, display: 'flex', gap: 24, flexWrap: 'wrap' }}>
        <div>
          <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>Vendor</Text>
          <Select
            style={{ width: 220 }}
            showSearch
            loading={optionsLoading}
            value={vendorId}
            onChange={handleVendorChange}
            options={vendorIdOptions}
            optionFilterProp="label"
          />
        </div>
        <div>
          <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>Tag</Text>
          <Select
            style={{ width: 220 }}
            value={tag}
            onChange={(next: string) => {
              setTransfers([]);
              setNextToken(undefined);
              setTag(next);
            }}
            options={tagSelectOptions(tagChoices)}
          />
        </div>
        <div>
          <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>Status</Text>
          <Select
            style={{ width: 260 }}
            value={status}
            onChange={handleStatusChange}
            options={[
              { value: 'all', label: 'All' },
              ...STATUS_FILTERS.map((value) => ({
                value,
                label: transferStatusDisplay(value).label,
              })),
            ]}
          />
        </div>
        <div>
          <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>Triggered by</Text>
          <Select
            style={{ width: 180 }}
            value={triggered}
            onChange={handleTriggeredChange}
            options={[
              { value: 'all', label: 'All' },
              { value: 'ad_hoc', label: 'ad hoc' },
            ]}
          />
        </div>
      </div>

      <Table<TransferItem>
        columns={columns}
        dataSource={visibleTransfers}
        rowKey={(row) => row.transferId}
        loading={loading}
        pagination={false}
        size="small"
        style={{ width: '100%' }}
        onRow={(record) => ({
          onClick: () => openTransfer(record),
          style: { cursor: 'pointer' },
        })}
      />

      {nextToken && (
        <div style={{ marginTop: 16, textAlign: 'center' }}>
          {filterNote && (
            <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>{filterNote}</Text>
          )}
          <Button onClick={() => void fetchPage(vendorId, status, triggered, tag, true, nextToken)} loading={loadingMore}>
            Load more
          </Button>
        </div>
      )}
    </>
  );
};

export default TransferList;
